import { MaskFormTextField } from '@exam-center/components';
import { BlockedUserIdType } from '@exam-center/graphql';
import { ID_CARD_NUMBER_MASK, PHONE_NUMBER_MASK } from '@exam-center/utils';
import {
  Flex,
  FormRadioSelection,
  FormSearchSelection,
  FormTextField,
  Grid,
  type OptionSelectionProps,
  ReadonlyField,
  SectionTitle,
  Text,
} from '@shared/components';
import { useWatch } from 'react-hook-form';

const ID_TYPE_OPTIONS = [
  { value: BlockedUserIdType.CitizenCard, label: 'บัตรประชาชน' },
  { value: BlockedUserIdType.Passport, label: 'พาสปอร์ต' },
];

interface BasicFields {
  idType: {
    name: string;
    required?: boolean;
  };
  idNumber: {
    name: string;
    required?: boolean;
  };
  title: {
    name: string;
    required?: boolean;
    options: OptionSelectionProps[];
  };
  firstName: {
    name: string;
    required?: boolean;
  };
  middleName: {
    name: string;
    required?: boolean;
  };
  lastName: {
    name: string;
    required?: boolean;
  };
  phoneNumber: {
    name: string;
    required?: boolean;
  };
}

export type PersonalInformationFormFieldsProps =
  | {
      mode: 'create';
      fields: BasicFields & {
        email: {
          name: string;
          required?: boolean;
        };
      };
    }
  | {
      mode: 'edit';
      fields: BasicFields & {
        email: string;
      };
    };

export default function PersonalInformationFormFields(
  props: Readonly<PersonalInformationFormFieldsProps>,
) {
  const { mode, fields } = props;

  const idType = useWatch({ name: fields.idType.name }) as BlockedUserIdType | undefined;
  const isPassport = idType === BlockedUserIdType.Passport;

  return (
    <Flex
      sx={(theme) => ({
        flexDirection: 'column',
        gap: theme.spacing(6),
        p: theme.spacing(6),
      })}
    >
      <SectionTitle title='Personal Information' />
      <Grid
        sx={(theme) => ({
          gridTemplateColumns: '240px 1fr',
          columnGap: theme.spacing(6),
          rowGap: theme.spacing(4),
        })}
      >
        <Text variant='body1Semibold'>ข้อมูลส่วนตัว</Text>
        <Flex
          sx={(theme) => ({
            gap: theme.spacing(4),
            flexDirection: 'column',
          })}
        >
          <FormRadioSelection row options={ID_TYPE_OPTIONS} {...fields.idType} />
          {isPassport ? (
            <FormTextField
              label='เลขที่พาสปอร์ต'
              placeholder='กรอกเลขที่พาสปอร์ต'
              sx={{ maxWidth: '600px' }}
              {...fields.idNumber}
            />
          ) : (
            <MaskFormTextField
              label='เลขที่บัตรประชาชน'
              placeholder='กรอกเลขบัตรประชาชน'
              sx={{ maxWidth: '600px' }}
              maskOptions={{
                mask: ID_CARD_NUMBER_MASK,
              }}
              {...fields.idNumber}
            />
          )}
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(4),
            })}
          >
            <FormSearchSelection
              label='คำนำหน้า'
              placeholder='เลือกคำนำหน้า'
              noOptionsText='ไม่พบคำนำหน้าดังกล่าว'
              sx={{
                minWidth: '260px',
              }}
              {...fields.title}
            />
            <FormTextField label='ชื่อ' placeholder='กรอกชื่อ' {...fields.firstName} />
            <FormTextField label='ชื่อกลาง' placeholder='กรอกชื่อกลาง' {...fields.middleName} />
            <FormTextField label='นามสกุล' placeholder='กรอกนามสกุล' {...fields.lastName} />
          </Flex>
        </Flex>
        <Text variant='body1Semibold'>ข้อมูลสำหรับติดต่อ</Text>
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(4),
            maxWidth: '600px',
          })}
        >
          {mode === 'create' ? (
            <FormTextField label='อีเมลผู้ใช้' placeholder='กรอกอีเมลผู้ใช้' {...fields.email} />
          ) : (
            <ReadonlyField label='อีเมลผู้ใช้' content={fields.email} />
          )}
          <MaskFormTextField
            label='เบอร์โทรศัพท์'
            placeholder='กรอกเบอร์โทรศัพท์ที่ทำงาน'
            maskOptions={{
              mask: PHONE_NUMBER_MASK,
            }}
            {...fields.phoneNumber}
          />
        </Flex>
      </Grid>
    </Flex>
  );
}
